import { useMemo } from 'react';
import { Box, FormControl, InputLabel, Select, MenuItem, IconButton, Tooltip } from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { QueryRule as QueryRuleType, QueryField } from '../../../../types/QueryBuilder';
import { getFieldsByCategory, getOperatorsForField } from '../../../../utils/queryBuilderUtils';
import ValueEditor from './ValueEditors';

interface QueryRuleProps {
    rule: QueryRuleType;
    categories: string[];
    fields: QueryField[];
    onUpdateRule: (ruleId: string, data: Partial<QueryRuleType>) => void;
    onRemoveRule: (ruleId: string) => void;
}

function QueryRule({
    rule,
    categories,
    fields,
    onUpdateRule,
    onRemoveRule,
}: QueryRuleProps) {
    const field = useMemo(
        () => fields.find(f => f.name === rule.field && f.Categorie === rule.category),
        [fields, rule.field, rule.category]
    );
    const availableFields = useMemo(
        () => rule.category ? getFieldsByCategory(fields, rule.category) : [],
        [fields, rule.category]
    );
    const operators = useMemo(() => getOperatorsForField(field), [field]);

    const handleCategoryChange = (category: string) => {
        const newFields = getFieldsByCategory(fields, category);
        const newField = newFields[0]?.name || '';
        onUpdateRule(rule.id, {
            category,
            field: newField,
            operator: '=',
            value: '',
            valueTo: undefined
        });
    };

    const handleFieldChange = (fieldName: string) => {
        const newField = fields.find(f => f.name === fieldName && f.Categorie === rule.category);
        onUpdateRule(rule.id, {
            field: fieldName,
            operator: (newField?.defaultOperator as any) || '=',
            value: '',
            valueTo: undefined
        });
    };

    const handleOperatorChange = (operator: string) => {
        onUpdateRule(rule.id, {
            operator: operator as any,
            ...(operator !== 'between' ? { valueTo: undefined } : {})
        });
    };

    const renderValueEditor = () => {
        if (!field) return null;

        return (
            <Box sx={{ flexGrow: 1, minWidth: '200px', maxWidth: 'calc(100% - 565px)' }}>
                <ValueEditor
                    ruleId={rule.id}
                    field={field}
                    operator={rule.operator || '='}
                    value={rule.value}
                    onChange={(value) => onUpdateRule(rule.id, { value })}
                />
            </Box>
        );
    };

    return (
        <Box
            sx={{
                display: 'flex',
                flexWrap: 'wrap',
                alignItems: 'center',
                gap: 1,
                mb: 2,
                p: 1.5,
                bgcolor: '#ffffff',
                borderRadius: 1,
                border: '1px solid #e0e0e0',
                '&:hover': {
                    borderColor: '#556ee6',
                    boxShadow: '0 2px 8px rgba(85, 110, 230, 0.1)'
                }
            }}
        >
            <FormControl size="small" sx={{ minWidth: 150 }}>
                <InputLabel>Catégorie</InputLabel>
                <Select
                    value={rule.category || ''}
                    label="Catégorie"
                    onChange={(e) => handleCategoryChange(e.target.value)}
                    MenuProps={{
                        PaperProps: {
                            style: {
                                maxHeight: 300,
                            },
                        },
                    }}
                >
                    {categories.map((cat) => (
                        <MenuItem key={cat} value={cat}>
                            {cat}
                        </MenuItem>
                    ))}
                </Select>
            </FormControl>

            <FormControl size="small" sx={{ minWidth: 200 }}>
                <InputLabel>Champ</InputLabel>
                <Select
                    value={rule.field || ''}
                    label="Champ"
                    onChange={(e) => handleFieldChange(e.target.value)}
                    disabled={!rule.category}
                    MenuProps={{
                        PaperProps: {
                            style: {
                                maxHeight: 300,
                            },
                        },
                    }}
                >
                    {availableFields.map((f) => (
                        <MenuItem key={f.name} value={f.name}>
                            {f.label}
                        </MenuItem>
                    ))}
                </Select>
            </FormControl>

            <FormControl size="small" sx={{ minWidth: 150 }}>
                <InputLabel>Opérateur</InputLabel>
                <Select
                    value={rule.operator || ''}
                    label="Opérateur"
                    onChange={(e) => handleOperatorChange(e.target.value)}
                    disabled={!rule.field}
                    MenuProps={{
                        PaperProps: {
                            style: {
                                maxHeight: 300,
                            },
                        },
                    }}
                >
                    {operators.map((op) => (
                        <MenuItem key={op.value} value={op.value}>
                            {op.label}
                        </MenuItem>
                    ))}
                </Select>
            </FormControl>

            {rule.field && rule.operator && renderValueEditor()}

            <Tooltip title="Supprimer la règle">
                <IconButton
                    size="small"
                    onClick={() => onRemoveRule(rule.id)}
                    sx={{
                        color: '#dc3545',
                        '&:hover': {
                            backgroundColor: 'rgba(220, 53, 69, 0.1)'
                        }
                    }}
                >
                    <DeleteOutlineIcon fontSize="small" />
                </IconButton>
            </Tooltip>
        </Box>
    );
};

export default QueryRule;
